import React from "react";
import { LoadPhase, SkeletonCard } from "../../../components/ui";
import MobileChartReadout from "./MobileChartReadout";
import { MONTHS } from "../types/dashboard.types";
import type { MonthlyClients } from "../types/dashboard.types";
import { BRAND, BRAND_DARK, EMPTY_DISC } from "../chartTheme";

/**
 * How the plot is proportioned.
 *
 * `PLOT_HEIGHT` is what the curve is drawn in, and `TARGET_HEIGHT` is what a
 * finger presses: the press strip is taller than the plot and hangs below it
 * over the month labels, which is the one band of the card with nothing else in
 * it to press. A 108px plot cut into twelve columns gives targets 27px across,
 * so the height is what has to make up the difference.
 *
 * `HEAD_ROOM` keeps the year's peak off the top edge, so the marker on it is a
 * dot rather than a clipped half of one, and `FLOOR_Y` keeps a month that took
 * nobody off the bottom one. Both in the plot's own 0-100 space.
 */
const PLOT_HEIGHT = 108;
const TARGET_HEIGHT = 132;
const HEAD_ROOM = 12;
const FLOOR_Y = 98;

/** The card's own footprint, so the block standing in for it is the size of what
 *  lands in its place: the heading, the count, the readout, the plot and the row
 *  of initials. */
const FOOTPRINT = "h-[232px] rounded-card";
const FLOOR = "min-h-[232px]";

interface MobileClientsTrendProps {
  /** A count per month of the current year, as the dashboard endpoint sends it.
   *  Empty until it arrives, which draws the shape a year will take rather than
   *  a curve the data has not confirmed. */
  data: MonthlyClients[];
  /** Whether anyone has joined yet — see `NewClientsCard`, which draws the same
   *  distinction on a wide screen. */
  empty?: boolean;
  /** Where the dashboard call is. */
  phase?: LoadPhase;
}

/**
 * Where a month's mark sits across the plot, as a fraction of its width.
 *
 * The middle of the month's own share of the row, not the fraction of the way
 * along it. That is what keeps the curve, the marker on it, the press targets and
 * the initials under them on one scale: the last three are equal cells of a flex
 * row and sit at their own centres, so a curve drawn from the first pixel to the
 * last would put every point half a month off the letter under it. A twelve-point
 * year is out by 4% at the ends, which is a mark sitting over its neighbour.
 *
 * It costs the plot half a month of air at either end, which is the same margin
 * the desktop cards' band axis leaves — and it means a single month lands in the
 * middle rather than hard against the left edge, where it would read as a curve
 * that had been cut off.
 */
const xFraction = (index: number, count: number) =>
  (index + 0.5) / count;

/**
 * The curve through the year, as an SVG path in a 0-100 box.
 *
 * Catmull-Rom control points turned into cubic beziers, which is the smoothing
 * a monotone line chart uses — a year of counts read as a trend rather than as a
 * dozen hinges. The tension is deliberately gentle: a curve that overshoots
 * between two points is drawing a month that did not happen.
 *
 * Returned in the plot's own square space and stretched to the card's width by
 * the viewBox, which is why the stroke below carries `non-scaling-stroke`.
 */
const curve = (points: { x: number; y: number }[]) => {
  if (points.length < 2) return "";

  let path = `M ${points[0].x} ${points[0].y}`;
  for (let i = 0; i < points.length - 1; i += 1) {
    const previous = points[i - 1] ?? points[i];
    const start = points[i];
    const end = points[i + 1];
    const next = points[i + 2] ?? end;
    // A sixth of the neighbouring span, which is the standard Catmull-Rom
    // conversion — enough to round the corner, not enough to invent a bump.
    const c1x = start.x + (end.x - previous.x) / 6;
    const c1y = start.y + (end.y - previous.y) / 6;
    const c2x = end.x - (next.x - start.x) / 6;
    const c2y = end.y - (next.y - start.y) / 6;
    path += ` C ${c1x} ${c1y} ${c2x} ${c2y} ${end.x} ${end.y}`;
  }
  return path;
};

/**
 * How many clients joined, month by month, as a phone reads it.
 *
 * Not `NewClientsCard` at a narrower width. That card is a Recharts area chart
 * with a twelve-tick axis and a hover tooltip, and neither survives the trip: at
 * 328px of card the twelve "Jan"/"Feb" ticks collide into a grey smear, and
 * there is no pointer to open the tooltip that would have made up for it. What
 * is left of the chart at this size is a shape and a way to ask a month for its
 * figure — so that is what this draws, and it draws it in about forty lines of
 * SVG rather than pulling a charting library onto a phone to do it. The desktop
 * card is untouched and still what a pointer gets.
 *
 * The curve is a path; everything that has to stay round or square — the marker
 * on the pressed month, the press targets, the labels — is laid over it in HTML
 * at the same fractions of the width. That is what lets the plot stretch to any
 * phone without the stroke thickening or the marker going oval.
 *
 * Leads with a count, because a trend with no total is a shape nobody can put a
 * size to. The initials are the axis a phone has room for, one letter per month,
 * with the pressed one inked — the same axis the earnings card above it draws,
 * so the two charts read as one pair rather than two conventions.
 */
const MobileClientsTrend: React.FC<MobileClientsTrendProps> = ({
  data,
  empty = false,
  phase = "ready",
}) => {
  /** Which month is being asked about, or `null` as the card opens. Pressing the
   *  month already selected clears it — the same gesture the earnings card
   *  above takes, since on a card this size there is nowhere neutral to press. */
  const [selected, setSelected] = React.useState<number | null>(null);

  /**
   * Whether the card draws the shape of a year instead of one.
   *
   * The same rule `NewClientsCard` goes by: `new_clients_by_month: null` is a
   * year the backend has not counted, which arrives here as no rows, and a flat
   * line along the floor reads as a year that went badly rather than as one that
   * has not started.
   */
  const showsEmptyYear = empty || data.length === 0;

  /** The twelve columns, or a year of flat months standing in for one nobody has
   *  counted — one list for the plot, the targets and the labels, so a short
   *  year can never put a mark under the wrong month. */
  const columns: MonthlyClients[] = showsEmptyYear
    ? MONTHS.map((month) => ({ month, clients: 0 }))
    : data;

  const joined = columns.reduce((sum, month) => sum + month.clients, 0);
  const peak = Math.max(...columns.map((month) => month.clients), 0);

  /** The year's busiest month, which is what the readout holds until a month is
   *  asked for by name — the one direct label the plot carries, since a figure
   *  over every point is the scatter that makes a small chart unreadable. */
  const busiest = React.useMemo(() => {
    if (showsEmptyYear || peak <= 0) return null;
    return columns.reduce((leader, month) =>
      month.clients > leader.clients ? month : leader
    );
  }, [columns, peak, showsEmptyYear]);

  /** Where a count sits down the plot, in the 0-100 space the path is drawn in.
   *
   *  `FLOOR_Y` rather than 100: a year in which nobody joined is a real answer
   *  and is plotted flat, and a flat line laid exactly on the bottom edge is a
   *  line with half its stroke outside the box. Two units up is the whole of
   *  what keeps it visible. */
  const yOf = (clients: number) =>
    peak <= 0 ? FLOOR_Y : FLOOR_Y - (clients / peak) * (FLOOR_Y - HEAD_ROOM);

  const points = columns.map((month, index) => ({
    x: xFraction(index, columns.length) * 100,
    y: yOf(month.clients),
  }));

  const line = curve(points);
  /** The same curve closed along the floor, which is what the wash fills. */
  const area =
    line &&
    `${line} L ${points[points.length - 1].x} 100 L ${points[0].x} 100 Z`;

  if (phase !== "ready") {
    return <SkeletonCard phase={phase} className={FOOTPRINT} />;
  }

  const shown = selected === null ? null : columns[selected] ?? null;

  return (
    <section
      className={`card-surface border border-line bg-white p-4 ${FLOOR}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {/* A workspace with nobody in it has no series to name, so the card
              says what it is waiting for instead of titling a chart that is not
              there — the same two headings the desktop card carries. */}
          <h2 className="text-[15px] font-semibold text-ink">
            {empty ? "Nothing to track yet" : "New clients"}
          </h2>
          {empty ? (
            <p className="mt-0.5 text-[12px] text-ink-muted">
              Your activity will grow as your practice does.
            </p>
          ) : (
            <p className="mt-1 flex items-baseline gap-1.5">
              <span className="text-[22px] font-semibold leading-none tracking-tight text-ink">
                {joined}
              </span>
              <span className="text-[12px] text-ink-muted">
                {showsEmptyYear ? "no data yet" : "joined this year"}
              </span>
            </p>
          )}
        </div>

        <div className="flex-shrink-0 pt-0.5">
          <MobileChartReadout
            label={shown?.month}
            value={
              shown
                ? `${shown.clients} ${shown.clients === 1 ? "client" : "clients"}`
                : null
            }
            swatch={BRAND_DARK}
            fallback={
              busiest && (
                <>
                  <span className="text-ink-subtle">Busiest</span>
                  <span className="font-medium text-ink-muted">
                    {busiest.month}
                  </span>
                </>
              )
            }
          />
        </div>
      </div>

      <div className="relative mt-3" style={{ height: PLOT_HEIGHT }}>
        {/* The curve. `preserveAspectRatio="none"` is what stretches the plot's
            square space across whatever width the phone gives, and
            `non-scaling-stroke` is what keeps the line 2px through it rather
            than a stroke squashed in one direction. Hidden from screen readers:
            the twelve buttons below carry the same year as twelve labelled
            figures. */}
        <svg
          aria-hidden
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          className="absolute inset-0 w-full h-full overflow-visible"
        >
          <defs>
            <linearGradient id="mobileClientsFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={BRAND} stopOpacity={0.3} />
              <stop offset="100%" stopColor={BRAND} stopOpacity={0} />
            </linearGradient>
          </defs>

          {showsEmptyYear ? (
            // The axis a curve will be drawn on, and nothing that could be read
            // as a month doing better or worse than another.
            <line
              x1={xFraction(0, columns.length) * 100}
              y1={FLOOR_Y}
              x2={xFraction(columns.length - 1, columns.length) * 100}
              y2={FLOOR_Y}
              stroke={EMPTY_DISC}
              strokeWidth={3}
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
          ) : (
            <>
              {area && <path d={area} fill="url(#mobileClientsFill)" />}
              {line && (
                <path
                  d={line}
                  fill="none"
                  stroke={BRAND}
                  strokeWidth={2}
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                />
              )}
            </>
          )}
        </svg>

        {/* A year one month old has no curve to draw — two points is the least a
            line can be made of — so the one month it does have is drawn as the
            point it is. The backend sends all twelve of a year, zeros included,
            so this is January of a workspace whose first month is still being
            counted rather than the usual case. */}
        {!showsEmptyYear && columns.length === 1 && (
          <span
            aria-hidden
            className="absolute w-2.5 h-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full"
            style={{
              left: `${xFraction(0, 1) * 100}%`,
              top: `${yOf(columns[0].clients)}%`,
              backgroundColor: BRAND,
            }}
          />
        )}

        {/* The marker on the pressed month, in HTML rather than in the stretched
            SVG so it stays a circle at every width. A ring of the surface
            colour around it, which is what keeps it off the curve it sits on. */}
        {!showsEmptyYear && shown && selected !== null && (
          <span
            aria-hidden
            className="absolute w-2.5 h-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full"
            style={{
              left: `${xFraction(selected, columns.length) * 100}%`,
              top: `${yOf(shown.clients)}%`,
              backgroundColor: BRAND_DARK,
              boxShadow: "0 0 0 2px #FFFFFF",
            }}
          />
        )}

        {/* The press strip: a column per month, the full height of the plot and
            a little past it, so a thumb has something worth aiming at. Over the
            labels rather than beside them — that band is the only part of the
            card with nothing else in it to press. */}
        <div
          role="group"
          aria-label="New clients by month"
          className="absolute inset-x-0 top-0 flex"
          style={{ height: TARGET_HEIGHT }}
        >
          {columns.map((month, index) => (
            <button
              key={`${month.month}-${index}`}
              type="button"
              // The month and its count, so the plot reads as a list of twelve
              // facts rather than as a shape — which is what the desktop card's
              // tooltip says, and why the curve itself is hidden instead.
              aria-label={`${month.month}: ${month.clients} new ${
                month.clients === 1 ? "client" : "clients"
              }`}
              aria-pressed={index === selected}
              onClick={() => setSelected(index === selected ? null : index)}
              className="flex-1 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
            >
              {/* The column being asked about is ruled from the curve down, the
                  way a crosshair marks a point — the one piece of chrome that
                  only appears once something has been pressed. */}
              {index === selected && !showsEmptyYear && (
                <span
                  aria-hidden
                  className="block w-px mx-auto"
                  style={{
                    marginTop: (yOf(month.clients) / 100) * PLOT_HEIGHT,
                    height:
                      PLOT_HEIGHT - (yOf(month.clients) / 100) * PLOT_HEIGHT,
                    backgroundColor: EMPTY_DISC,
                  }}
                />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* The axis, at the one size a phone has room for: an initial a month.
          Hidden from screen readers — the buttons above carry the month names
          already, and a second row of twelve letters is the axis read twice. */}
      <div aria-hidden className="mt-2 flex">
        {columns.map((month, index) => (
          <span
            key={`${month.month}-${index}`}
            className={`flex-1 text-center text-[10px] leading-none ${
              index === selected
                ? "font-semibold text-brand-dark"
                : "font-medium text-ink-subtle"
            }`}
          >
            {month.month.charAt(0)}
          </span>
        ))}
      </div>
    </section>
  );
};

export default MobileClientsTrend;
